import React from 'react';
import { Box, IconButton as MuiIconButton, styled, TableCell, TableRow, useTheme } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton } from '../../atoms';
import { ReactComponent as EditIcon } from '../../../assets/icons/pencil_acc.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/trash.svg';
import type { FieldValues } from 'react-hook-form';
import type { EditableColumnDef } from './types';

const StyledRow = styled(TableRow)<{ $rowHeight?: string; $isExpanded?: boolean; $error?: boolean }>(
    ({ theme, $rowHeight, $isExpanded, $error }) => ({
        height: $rowHeight || '48px',
        ...($isExpanded && {
            backgroundColor: theme.palette?.primary[25]
        }),
        '& > .MuiTableCell-root': {
            borderTop: `1px solid ${theme.palette.semantic.bd.default}`
        },
        '& > .MuiTableCell-root:first-of-type': {
            borderLeft: $error
                ? `3px solid ${theme.palette.semantic.bd.error}`
                : `1px solid ${theme.palette.semantic.bd.default}`
        },
        '& > .MuiTableCell-root:last-of-type': {
            borderRight: `1px solid ${theme.palette.semantic.bd.default}`
        },
        '&:last-of-type > .MuiTableCell-root': {
            borderBottom: `1px solid ${theme.palette.semantic.bd.default}`
        },
        '&:last-of-type > .MuiTableCell-root:first-of-type': {
            borderBottomLeftRadius: '8px'
        },
        '&:last-of-type > .MuiTableCell-root:last-of-type': {
            borderBottomRightRadius: '8px'
        }
    })
);

const ExpandableCellContent = styled('span', {
    shouldForwardProp: (prop) => prop !== '$disabled'
})<{ $disabled?: boolean }>(({ theme, $disabled }) => ({
    cursor: $disabled ? 'not-allowed' : 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    opacity: $disabled ? 0.5 : 1,
    '&:hover': {
        opacity: $disabled ? 0.5 : 0.8
    }
}));

interface EditableTableRowProps<T extends FieldValues> {
    row: T;
    rowKey: string | number;
    columns: EditableColumnDef<T>[];
    isExpanded: boolean;
    expandable?: boolean;
    // eslint-disable-next-line no-unused-vars
    onToggleExpand: (key: string | number, isReadOnlyToggle?: boolean) => void;
    onEdit?: () => void;
    onDelete?: () => void;
    showActions: boolean;
    actionsColumnWidth?: string;
    rowHeight?: string;
    bodyCellVerticalAlign?: 'top' | 'middle';
    editDisabled?: boolean;
    ariaLabels?: { edit?: string; delete?: string };
    lockedByOther?: boolean;
    hasError?: boolean;
}

export const EditableTableRow = <T extends FieldValues>({
    row,
    rowKey,
    columns,
    isExpanded,
    expandable = false,
    onToggleExpand,
    onEdit,
    onDelete,
    showActions,
    actionsColumnWidth = '120px',
    rowHeight,
    bodyCellVerticalAlign = 'middle',
    editDisabled,
    ariaLabels,
    lockedByOther,
    hasError
}: EditableTableRowProps<T>) => {
    const theme = useTheme();
    return (
        <StyledRow theme={theme} $rowHeight={rowHeight} $isExpanded={isExpanded} $error={hasError}>
            {columns.map((column, colIndex) => {
                const value = row[column.key as keyof T];
                const content = column.render ? column.render(value, row) : <>{value ?? '-'}</>;
                const isFirstWithChevron = expandable && colIndex === 0;

                if (column.expandable) {
                    const isExpandDisabled =
                        !!lockedByOther && ((row as { contributions?: unknown[] }).contributions?.length ?? 0) === 0;
                    return (
                        <TableCell
                            key={column.key}
                            align={column.align ?? 'left'}
                            sx={{
                                width: column.width,
                                verticalAlign: bodyCellVerticalAlign,
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '20px',
                                borderBottom: isExpanded ? `1px solid ${theme.palette.semantic.bd.subtle}` : 'none'
                            }}
                        >
                            <ExpandableCellContent
                                role="button"
                                tabIndex={0}
                                $disabled={isExpandDisabled}
                                onClick={(e) => {
                                    if (isExpandDisabled) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                    }
                                    onToggleExpand(rowKey, false);
                                }}
                            >
                                {content}
                            </ExpandableCellContent>
                        </TableCell>
                    );
                }

                return (
                    <TableCell
                        key={column.key}
                        align={column.align ?? 'left'}
                        sx={{
                            width: column.width,
                            verticalAlign: bodyCellVerticalAlign,
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '20px',
                            borderBottom: isExpanded ? `1px solid ${theme.palette.semantic.bd.default}` : 'none'
                        }}
                    >
                        {isFirstWithChevron ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <MuiIconButton
                                    size="small"
                                    onClick={() => onToggleExpand(rowKey, true)}
                                    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                                    sx={{ padding: '2px' }}
                                >
                                    {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                </MuiIconButton>
                                <Box>{content}</Box>
                            </Box>
                        ) : (
                            content
                        )}
                    </TableCell>
                );
            })}
            {showActions && (
                <TableCell
                    align="center"
                    sx={{
                        width: actionsColumnWidth,
                        verticalAlign: bodyCellVerticalAlign,
                        borderBottom: isExpanded ? `1px solid ${theme.palette.semantic.bd.default}` : 'none'
                    }}
                >
                    <Box display="flex" gap={1} justifyContent="center">
                        {onEdit && (
                            <IconButton
                                iconName={<EditIcon color="primary" />}
                                bordered={false}
                                onClick={onEdit}
                                disabled={editDisabled}
                                arialabel={ariaLabels?.edit}
                                color="primary"
                                sx={{
                                    opacity: editDisabled ? 0.5 : 1,
                                    pointerEvents: editDisabled ? 'none' : 'auto'
                                }}
                            />
                        )}
                        {onDelete && (
                            <IconButton
                                iconName={<DeleteIcon color="primary" />}
                                bordered={false}
                                onClick={onDelete}
                                disabled={editDisabled}
                                arialabel={ariaLabels?.delete}
                                color="primary"
                                sx={{
                                    opacity: editDisabled ? 0.5 : 1,
                                    pointerEvents: editDisabled ? 'none' : 'auto'
                                }}
                            />
                        )}
                    </Box>
                </TableCell>
            )}
        </StyledRow>
    );
};
